/**
 * Route Guards for Authentication
 * Handles route protection and authentication state checking
 */

import type { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import { useAuthStore } from '../stores/auth'

export interface RouteGuard {
  requiresAuth(to: RouteLocationNormalized): boolean
  redirectToLogin(): void
  checkAuthState(): Promise<boolean>
}

/**
 * Authentication guard middleware for protected routes
 * @param to - Target route
 * @param from - Source route
 * @param next - Navigation guard next function
 */
export const authGuard = async (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const authStore = useAuthStore()

  // Check if route requires authentication
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

  if (requiresAuth) {
    // Always initialize auth if not authenticated, regardless of loading state
    if (!authStore.isAuthenticated) {
      await authStore.initializeAuth()
    }

    // Check if user is authenticated after initialization
    if (!authStore.isAuthenticated) {
      // Store the intended destination for redirect after login
      const redirectPath = to.fullPath !== '/login' && to.fullPath !== '/register' 
        ? to.fullPath 
        : '/meals'
      
      next({
        path: '/login',
        query: { redirect: redirectPath }
      })
      return
    }
  }

  // If user is authenticated and trying to access auth pages, redirect to dashboard
  if (authStore.isAuthenticated && (to.path === '/login' || to.path === '/register')) {
    next('/meals')
    return
  }

  next()
}

/**
 * Helper function to check if a route requires authentication
 * @param to - Target route
 * @returns boolean indicating if authentication is required
 */
export const requiresAuth = (to: RouteLocationNormalized): boolean => {
  return to.matched.some(record => record.meta.requiresAuth)
}

/**
 * Helper function to redirect to login with current path
 * @param currentPath - Current route path
 * @returns Login route object with redirect query
 */
export const createLoginRedirect = (currentPath?: string) => {
  const redirectPath = currentPath && currentPath !== '/login' && currentPath !== '/register' 
    ? currentPath 
    : '/meals'
  
  return {
    path: '/login',
    query: { redirect: redirectPath }
  }
}

/**
 * Helper function to get redirect path from query or default
 * @param query - Route query parameters
 * @param defaultPath - Default redirect path
 * @returns Redirect path string
 */
export const getRedirectPath = (query: any, defaultPath = '/meals'): string => {
  const redirect = query.redirect
  return typeof redirect === 'string' ? redirect : defaultPath
}

/**
 * Check authentication state and initialize if needed
 * @returns Promise<boolean> indicating if user is authenticated
 */
export const checkAuthState = async (): Promise<boolean> => {
  const authStore = useAuthStore()
  
  try {
    if (!authStore.isAuthenticated && !authStore.isLoading) {
      await authStore.initializeAuth()
    }
    return authStore.isAuthenticated
  } catch (error) {
    console.error('Auth state check failed:', error)
    return false
  }
}
